import { useId, useState } from "react";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import { ChevronDown, ChevronRight } from "lucide-react";
import { SidebarProfileCard } from "./sidebar/profile-card";
import { SidebarFooter } from "./sidebar/footer";
import { type NavGroup, type NavItem } from "./sidebar/nav-items";
import { canSearchNav, filterNavGroups, firstNavItem } from "./sidebar/nav-search";
import { NavSearchInput } from "./sidebar/nav-search-input";
import { RECEPTION_DAILY_GROUP } from "./sidebar/reception-daily-group";
import { CSKH_DAILY_GROUP } from "./sidebar/cskh-daily-group";
import { BRAND_ART, BrandBand } from "./BrandArt";
import { useSealedInboxFlag } from "@/features/sealed-inbox/flag";

type Identity = { id: string; name: string; role: string };
type RoleDisplay = { label: string; color: string };

type Props = {
  identity: Identity | null;
  role: RoleDisplay;
  visibleGroups: NavGroup[];
  pendingCount: number;
  customerBookingPendingCount: number;
  /** Khách đang chờ trả lời trên mục "Chat khách" (QĐ 0271); bỏ trống = không vẽ. */
  chatWaitingCount?: number;
  /** Thông báo chưa đọc trên mục "Thông báo của tôi" — cùng số với chuông; bỏ trống = không vẽ. */
  notificationUnreadCount?: number;
  onNavigate?: () => void;
};

/**
 * Mục mang con số đếm NGƯỜI / TIN (có thể rất lớn): quá 99 thì ghi "99+" cho ô
 * số không đẩy vỡ dòng menu, và đọc màn hình nói đủ số kèm nghĩa của nó.
 */
const COUNT_LABELS: Record<string, (n: number) => string> = {
  customer_chats: (n) => `${n} khách đang chờ`,
  my_notifications: (n) => `${n} thông báo chưa đọc`,
};

/** Trang đang mở thuộc mục này (kể cả trang con, trang gộp làm thẻ của nó). */
function coversPath(n: NavItem, pathname: string): boolean {
  return [n.to, ...(n.alt ?? []).map((a) => a.to)].some(
    (to) => pathname === to || pathname.startsWith(`${to}/`),
  );
}

export function SidebarContent({
  identity,
  role,
  visibleGroups,
  pendingCount,
  customerBookingPendingCount,
  chatWaitingCount = 0,
  notificationUnreadCount = 0,
  onNavigate,
}: Props) {
  // Hộp thư kín (QĐ 0272): người gửi không nhận chuông (Câu 11a) — viên đỏ
  // trên menu là đường duy nhất họ biết thư kín danh có phản hồi.
  const sealedSenderUnread = useSealedInboxFlag((f) => f.senderUnread);
  const sealedReaderUnread = useSealedInboxFlag((f) => f.readerUnread);
  // Ô "Khác" (CEO chốt 2a, 27/09/2026): nhóm mang cờ `folded` — hiện chỉ vai
  // `qc`, xem qc-quality-group.ts — gom vào một ô thu gọn cuối menu. Vai khác
  // không nhóm nào mang cờ nên menu vẽ y như cũ.
  // Ô "Tìm menu" chỉ cho Tổng Giám đốc / Giám đốc — xem nav-search.tsx.
  const searchable = canSearchNav(identity?.role);
  const [query, setQuery] = useState("");
  const searching = searchable && query.trim() !== "";
  const groups = searching ? filterNavGroups(visibleGroups, query) : visibleGroups;
  const navigate = useNavigate();
  const openFirstMatch = () => {
    const n = searching ? firstNavItem(groups) : undefined;
    if (!n) return;
    setQuery("");
    void navigate({ to: n.to, ...(n.search ? { search: n.search } : {}) } as never);
    onNavigate?.();
  };
  const handleNavigate = () => {
    setQuery("");
    onNavigate?.();
  };
  const shown = groups.filter((g) => !g.folded);
  const folded = groups.filter((g) => g.folded);
  const foldedCount = folded.reduce((n, g) => n + g.items.length, 0);
  const regionId = useId();
  const { pathname } = useLocation();
  const activeInFolded = folded.some((g) => g.items.some((n) => coversPath(n, pathname)));
  // Mở sẵn khi trang đang mở nằm trong "Khác": không thì cột menu chẳng mục nào
  // sáng (và scroll-active-into-view không có mốc để cuộn tới). Đi VÀO trang như
  // thế thì mở ra; rời đi thì giữ nguyên lựa chọn của người dùng. Bấm thu gọn
  // vẫn được kể cả khi đang đứng ở trang trong đó — ô không bị khoá mở.
  const [open, setOpen] = useState(activeInFolded);
  const [seenPath, setSeenPath] = useState(pathname);
  if (seenPath !== pathname) {
    setSeenPath(pathname);
    if (activeInFolded) setOpen(true);
  }

  // Nhóm "Việc hằng ngày" (lễ tân, CSKH) nổi nhẹ trên nền để mắt tìm ra ngay
  // (bản mẫu CEO duyệt 27/09/2026) — xem reception-daily-group.ts,
  // cskh-daily-group.ts.
  const isDaily = (g: NavGroup) => g.id === RECEPTION_DAILY_GROUP.id || g.id === CSKH_DAILY_GROUP.id;

  const renderGroup = (g: NavGroup) => (
    <div key={g.id} className={isDaily(g) ? "rounded-xl bg-white/10 pb-1.5 pt-1" : undefined}>
      <div
        className={
          isDaily(g)
            ? "px-3 py-1 text-[10px] font-bold uppercase tracking-[1.5px] text-white"
            : "px-3 py-1 text-[10px] font-bold uppercase tracking-[1.5px] text-white/95"
        }
      >
        {g.label}
      </div>
      <div className="mt-1 space-y-0.5">
        {g.items.map((n) => {
          const Icon = n.icon;
          const badgeCount =
            n.id === "salary_rules"
              ? pendingCount
              : n.id === "cskh_booking"
                ? customerBookingPendingCount
                : n.id === "customer_chats"
                  ? chatWaitingCount
                  : n.id === "my_notifications"
                    ? notificationUnreadCount
                    : n.id === "sealed_inbox"
                      ? sealedSenderUnread
                      : n.id === "sealed_inbox_reader"
                        ? sealedReaderUnread
                        : 0;
          const showBadge = badgeCount > 0;
          const countLabel = COUNT_LABELS[n.id];
          return (
            <Link
              key={n.id}
              to={n.to}
              {...(n.search ? { search: n.search } : {})}
              className="flex items-center gap-3 rounded-md px-3 py-1.5 text-[13px] font-medium text-white/95 transition-colors hover:bg-black/10 hover:text-white"
              activeOptions={{ exact: !n.search, includeSearch: Boolean(n.search) }}
              activeProps={{
                // Cream pill + gold foil bar on the left edge (mockup 28/09/2026).
                className:
                  "relative bg-[#FFFEF5]! font-semibold text-[#6B5010]! before:absolute before:inset-y-2 before:-left-2 before:w-[3px] before:rounded-full before:bg-(image:--gn-foil)",
                // Mốc để cột menu tự cuộn tới mục đang mở khi tải trang;
                // xem scroll-active-into-view.ts.
                "data-nav-active": "",
              }}
              onClick={handleNavigate}
            >
              <Icon size={16} className="shrink-0" />
              <span className="truncate">{n.label}</span>
              {showBadge && (
                <span
                  className="ml-auto rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white"
                  {...(countLabel ? { "aria-label": countLabel(badgeCount) } : {})}
                >
                  {countLabel && badgeCount > 99 ? "99+" : badgeCount}
                </span>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );

  return (
    <>
      {/* Official wordmark in cream (mockup 28/09/2026). CEO 28/09: no G
          monogram on the left, wordmark 35px tall (~120px wide) with "ERP"
          beside it, bottom-aligned to the LETTERS' baseline — it sits at 88%
          of the wordmark's height (the dot under "Ạ" hangs below). The 3px /
          2px lift is measured on screen so the ERP glyphs end on the same
          pixel row as "GẠO NÂU" (the body font floor makes ERP 11.5px on
          phones). Then a thin rice-grain strip. */}
      <div className="flex items-end gap-2 px-4 pb-3 pt-4">
        <img
          src="/images/brand/gao-nau-wordmark-kem.svg"
          alt="GẠO NÂU"
          className="h-[35px] w-auto min-w-0"
        />
        <span className="mb-[3px] shrink-0 text-[10px] font-semibold uppercase leading-none tracking-[2px] text-white/95 md:mb-[2px]">ERP</span>
      </div>
      <BrandBand src={BRAND_ART.riceBand} height={9} className="mx-5 shrink-0 text-[#D9C28A]/70" />

      {identity && <SidebarProfileCard identity={identity} role={role} />}
      {searchable ? <NavSearchInput value={query} onChange={setQuery} onSubmit={openFirstMatch} /> : null}

      <nav className="flex-1 space-y-3 px-2 py-3">
        {searching && shown.length === 0 && folded.length === 0 ? (
          <p className="px-3 py-2 text-[13px] text-white/80">Không có menu nào khớp</p>
        ) : null}
        {shown.map(renderGroup)}
        {folded.length > 0 ? (
          <>
            <button
              type="button"
              aria-expanded={open}
              aria-controls={regionId}
              onClick={() => setOpen((o) => !o)}
              className={`flex w-full items-center justify-between gap-2 rounded-md border border-dashed border-white/30 px-3 py-2 text-left text-[13px] font-semibold text-white transition-colors hover:bg-black/10 hover:text-white ${open ? "bg-black/15" : "bg-black/10"}`}
            >
              <span>Khác · {foldedCount} mục</span>
              <span className="flex shrink-0 items-center gap-1 text-[11px] font-medium text-white/95">
                {open ? (
                  <ChevronDown size={14} aria-hidden className="shrink-0" />
                ) : (
                  <ChevronRight size={14} aria-hidden className="shrink-0" />
                )}
                {open ? "thu gọn" : "mở ra"}
              </span>
            </button>
            {open ? (
              <div id={regionId} className="space-y-3">
                {folded.map(renderGroup)}
              </div>
            ) : null}
          </>
        ) : null}
      </nav>

      <SidebarFooter />
    </>
  );
}
